<template>
    <div>
        <hsc-menu-item v-if="!disableExport(exportTypeEnum.Image)">
            <div slot="body" @click="download('png')">
                <!-- 'Export.Types.Image' -> "Image" -->
                <b-icon icon="image" />
                {{ $t('Export.Types.Image') }}
            </div>
        </hsc-menu-item>
        <hsc-menu-item v-if="!disableExport(exportTypeEnum.Excel)">
            <div slot="body" @click="download('Excel')">
                <!-- 'Export.Types.Excel' -> "Excel" -->
                <b-icon icon="file-earmark-excel" />
                {{ $t('Export.Types.Excel') }}
            </div>
        </hsc-menu-item>
        <hsc-menu-item v-if="!(disableExport(exportTypeEnum.WordPortrait) && disableExport(exportTypeEnum.WordLandscape))">
            <div slot="body">
                <!-- 'Export.Types.Word' -> "Word" -->
                <b-icon icon="file-earmark-word" />
                {{ $t('Export.Types.Word') }}
            </div>
            <hsc-menu-item v-if="!disableExport(exportTypeEnum.WordPortrait)">
                <div slot="body" @click="download('Word/Portrait')">
                    <!-- 'Export.Orientation.Portrait' -> "Portrait" -->
                    <b-icon icon="file-word" />
                    {{ $t('Export.Orientation.Portrait') }}
                </div>
            </hsc-menu-item>
            <hsc-menu-item v-if="!disableExport(exportTypeEnum.WordLandscape)">
                <div slot="body" @click="download('Word/Paysage')">
                    <!-- 'Export.Orientation.Landscape' -> "Paysage" -->
                    <b-icon icon="file-word" rotate="270" />
                    {{ $t('Export.Orientation.Landscape') }}
                </div>
            </hsc-menu-item>
        </hsc-menu-item>
    </div>
</template>
<script lang="ts">
import { Prop, Vue } from "vue-property-decorator";
import Component from 'vue-class-component';
import Dictionary from "@/types/Dictionary";
import axios from "axios";
import { API_URL } from "@/App.settings";
import { Canvg } from "canvg";
import Download from '@/types/Download';
import DownloadStateEnum from '@/enums/DownloadStateEnum';
import locales from './locales/ExportWork.json'
import ExportTypeEnum from "@/enums/ExportTypeEnum";

/** Composant d'export de composant Work */
@Component({ i18n: { messages: locales } })
export default class ExportWork extends Vue {
    public exportTypeEnum = ExportTypeEnum;

    /** Id de l'élément html contenant le ChartWork pour exporter en Svg le Chart existant */
    @Prop()
    public ExportWorkId!: string;

    /** Indique le contrôleur cible */
    @Prop()
    public TargetWebAPI!: string

    /** Arguments supplémentaires pour la méthode download */
    @Prop()
    public ExtrasArgs!: Dictionary<any>;

    /** Indique si les items du menu sont désactivés ou non */
    @Prop()
    public Disabled!: boolean;

    /** Configuration du Work */
    @Prop()
    public Configs!: any;

    /** Liste des exports activés */
    @Prop({ default: () => [] })
    public ExportTypesEnabled!: ExportTypeEnum[];

    /**
     * Désactivation de l'export par type selon l'attribut AnalysisMetaData mis côté server
     * @param exportType Type d'export
     */
    private disableExport(exportType: ExportTypeEnum): boolean {
        return this.Disabled || (this.ExportTypesEnabled.findIndex(expType => expType == exportType) == -1);
    }

    /**
     * Téléchargement
     * @param option Options de téléchargement ('png', 'Excel', 'Word/Portrait' ou 'Word/Paysage')
     */
    private async download(option: string) {
        /** Fermeture du menu de paramétrage */
        this.$emit('CloseExportMenuEvent');

        let maintenanceMessage: string = "";

        if(this.Configs.Periods[0] != null && this.Configs.Periods[0].Type.includes('DateToDate')) {
           maintenanceMessage = "le paramétrage Date à Date";
        }   

        if(maintenanceMessage != "") {
            this.$bvToast.toast("L'export pour " + maintenanceMessage + ", n'est pas disponible pour le moment.", {
                title: 'Maintenance',
                autoHideDelay: 5000,
                variant: "warning",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
            return;
        }

        let args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["Option"] = option;

        const canvas: HTMLCanvasElement = window.document.createElement("canvas");

        var chartWork: HTMLElement = window.document.getElementById(this.ExportWorkId + "ExportWork-ChartWork");
        console.debug("ExportWork - chartWork : %O - this.ExportWorkId : %O", chartWork, this.ExportWorkId + "ExportWork-ChartWork");

        /** @todo Gérer le chartWork dans une classe spécifique selon un contexte
         * @see https://refactoring.guru/fr/design-patterns/strategy (pattern Strategie) */
        if (chartWork != null) {
        
            var echarts: HTMLCollectionOf<Element> = chartWork.getElementsByClassName("vue-echarts-inner")

            // Annuler l'export si aucun graphique n'est présent
            if (echarts.length == 0) {
                this.$bvToast.toast(this.$t('NoChartToExport').toString(), {
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
                return;
            }

            var svgElement: SVGSVGElement = echarts.item(0).getElementsByTagName("svg").item(0);

            var svgNode: HTMLElement = (svgElement.cloneNode(true) as HTMLElement);

            svgNode.childNodes.item(1).childNodes.forEach(svg => {
                if ((svg as HTMLElement).attributes["d"] !== undefined) {
                    if (
                        !(svg as HTMLElement).attributes["d"].nodeValue.includes("0A1") &&
                        ((svg as HTMLElement).attributes["d"].nodeValue.includes("M0 ")
                            || (svg as HTMLElement).attributes["d"].nodeValue.includes("M1 ")
                            || (svg as HTMLElement).attributes["d"].nodeValue.includes("M2 "))
                    ) {
                        (svg as HTMLElement).style.display = "none";
                    }
                }

                if ((svg as HTMLElement).attributes["stroke-opacity"]?.nodeValue.includes("0.8"))
                    (svg as HTMLElement).setAttribute("stroke-opacity", "0")
            });

            const canvg = Canvg.from(canvas.getContext('2d'), (svgNode as HTMLElement).outerHTML);
            (await canvg).start();

            const pngData = canvas.toDataURL("image/png", 1);
            const img = new Image();
            img.src = pngData;

            args["Base64"] = img.src;
        }

        // Si des couleurs de graphiques sont présentes dans Configs alors on les envoi sinon on prend celles du thème
        if (this.Configs.WorkParameters?.ChartWorkParameters?.colors) {
            console.debug("ExportWork - colors : ", this.Configs.WorkParameters.ChartWorkParameters.colors);
            args["Colors"] = this.Configs.WorkParameters.ChartWorkParameters.colors;
        }
        else {
            /** Récupération des couleurs du thème du graphique */
            let theme = await import("@/assets/ECharts/themes/" + this.$store.getters['SessionStore/GetGraphTheme'] + ".json")
            args["Colors"] = JSON.stringify(theme.theme.color);
        }

        /** Ajoute les arguments supplémentaires du composant parents */
        if (this.ExtrasArgs)
            args = { ...args, ...this.ExtrasArgs };

        let type: string = "";

        if (option.includes('Excel')) {
            type = ".xlsx";
        }
        else if (option.includes("Word")) {
            type = ".docx";
        }
        else if (option.includes("png")) {
            type = ".png";
        }

        let index: number = this.$store.getters['DownloadingStore/GetLastIndex'];
        let download: Download = { Index: ++index, Name: this.$t('AnalysisExport') + ' (' + index + ')' + type, Type: type, State: DownloadStateEnum.InProgress, Data: "", Direct: true }
        this.$store.dispatch('DownloadingStore/AddDownloading', download);

        if (option.includes("png")) {
            canvas.toBlob((blob) => {
                this.$store.dispatch('DownloadingStore/AddData', { index, data: blob });
                this.$store.dispatch('DownloadingStore/UpdateState', { index, state: DownloadStateEnum.Finished })
            })
        }
        else {
            await axios.post(API_URL + this.TargetWebAPI + '/Download', args, { responseType: 'blob' }).then((response) => {
                let results: Dictionary<any> = response.data;
                this.$store.dispatch('DownloadingStore/AddData', { index, data: results });
                this.$store.dispatch('DownloadingStore/UpdateState', { index, state: DownloadStateEnum.Finished });
            })
                .catch(err => {
                    // AnErrorOccuredContactAdmin -> "Une erreur est survenue. Veuillez contacter votre administrateur."
                    this.$store.dispatch('DownloadingStore/UpdateState', { index, state: DownloadStateEnum.Error })
                    this.$bvToast.toast(this.$t('AnErrorOccuredContactAdmin').toString(), {
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
                })
        }
    }
}
</script>
<style scoped></style>